import { IQService, IHttpService, IWindowService, IPromise } from "angular";
import { FileService } from "../file.service";
import { PermissionGrant, PermissionDescription } from "@herbjoy/apex-api-models";

/**
 * A service used to get or modify permissions defined or granted in the system.
 */
export class PermissionGrantService {
    private cache: { permissionGrants?: PermissionGrant[] }
    private deferredPermissionGrants: IPromise<PermissionGrant[]>;
    constructor(private $http: IHttpService, private $q: IQService,
        private $window: IWindowService, private fileService: FileService,
        private apexApiUrl: string) {
        'ngInject';
        // Allow multiple concurrent requests to wait on the same HTTP request for permissions.
        this.deferredPermissionGrants = null;
        this.cache = {};
    }

    /**
     * Get the list of all permissions described for use in Apex.
     */
    retrievePermissionDescriptions() {
        return this.$http<PermissionDescription[]>({
            method: "GET",
            url: `${this.apexApiUrl}/permissions`
        }).then(response => response.data);
    }

    /**
     * Get the list of all permissions granted to the authenticated user.
     * @param {{ icdOfficeID?: number, divisionID?: number }} params Optionally check permission in the context
     *   of a specific division or office.
     */
    retrievePermissionGrants(params?: { icdOfficeID?: number, divisionID?: number}) {
        return this.$http<PermissionGrant[]>({
            method: "GET",
            url: `${this.apexApiUrl}/auth/permissions`,
            params
        }).then(response => response.data);
    }

    exportAllClaimPermissionsCsv() {
        return this.$q(resolve => {
            this.$window.location.href = `${this.apexApiUrl}/claims/all/permissions.csv`;
            resolve();
        });
    }

    importAllClaimPermissionsCsv(csvString) {
        const claimPermissions = csvString.trim().split("\n")
            .slice(1) // Omit the header line.
            .map(csvLine => csvLine.trim().split(","))
            .map(([claimType, claimValue, permissionKey, value]) => ({
                ClaimType: claimType,
                ClaimValue: claimValue,
                PermissionKey: permissionKey,
                Value: value
            }));
        return this.$http({
            method: "PUT",
            url: `${this.apexApiUrl}/claims/all/permissions`,
            data: claimPermissions
        });
    }

    /**
     * Return whether the user has a specific permission
     * @param {string} permissionKey The permission to be checked.
     * @param {{ icdOfficeID?: number, divisionID?: number }} params Optionally check permission in the context
     *   of a specific division or office.
     */
    hasPermission(permissionKey, params) {
        let permissionGrants = null;
        if (!params && this.cache.permissionGrants) {
            //TODO: Cache expiration.
            permissionGrants = this.$q.resolve(this.cache.permissionGrants);
        } else if (params) {
            // For now, if params are given, perform an HTTP requet each time.
            permissionGrants = this.retrievePermissionGrants(params);
        } else if (this.deferredPermissionGrants) {
            permissionGrants = this.deferredPermissionGrants;
        } else {
            this.deferredPermissionGrants = this.retrievePermissionGrants()
                .then(permissionGrants => {
                    this.deferredPermissionGrants = null;
                    this.cache.permissionGrants = permissionGrants;
                    return permissionGrants;
                });
            permissionGrants = this.deferredPermissionGrants;
        }
        return permissionGrants.then(permissionGrants => {
            const keyGrants = permissionGrants.filter(p => p.Description.Key === permissionKey);
            // Permission is granted if the permission appears and the last appearance is "true". A permission grant
            // can appear multiple times, and still be revoked if the final appearance is "false".
            return keyGrants.length && keyGrants[keyGrants.length - 1].Value === "true";
        });
    }

    /**
     * Get the expanded collection of claims that would be generated when signing
     * in the given user.
     * @param {number} userID The user ID
     */
    retrieveUserClaims(userID) {
        return this.$http({
            method: "GET",
            url: `${this.apexApiUrl}/employees/${userID}/claims`
        }).then(response => response.data)
    }

    /**
     * Get the list of user permission grants given to the user.
     * @param {number} userID The user ID
     */
    retrieveUserPermissions(userID) {
        return this.$http({
            method: "GET",
            url: `${this.apexApiUrl}/employees/${userID}/permissions`
        }).then(response => response.data)
    }

    /**
     * Create or update an overriding permission grant for a specific user on a specific
     * permission.
     * @param {number} userID The user ID owning the permission grant
     * @param {string} permissionKey The key identifying the permission that was granted.
     * @param {any} request The details of the permission grant request.
     */
    setUserPermission(userID, permissionKey, request) {
        return this.$http({
            method: "PUT",
            url: `${this.apexApiUrl}/employees/${userID}/permissions/${btoa(permissionKey)}`,
            data: request
        })
    }

    /**
     * Delete the custom value given to the user for a specific permission. This does not
     * affect permissions granted by other means.
     * @param {number} userID The user ID owning the permission grant
     * @param {string} permissionKey The key identifying the permission that was granted.
     */
    deleteUserPermission(userID, permissionKey) {
        return this.$http({
            method: "DELETE",
            url: `${this.apexApiUrl}/employees/${userID}/permissions/${btoa(permissionKey)}`
        })
    }

    /**
     * Simulate what kind of permissions would be granted to a user who had
     * the provided list of claims granted to them.
     * @param {any} claims The list of claims from which permissions should be generated
     */
    simulationPermissionGrantsForClaims(claims) {
        return this.$http({
            method: "POST",
            url: `${this.apexApiUrl}/services/permissions`,
            data: claims
        }).then(response => response.data)
    }

    /**
     * Get list of permissions granted for that claimType and claimValue
     * @param {number} claimType The Claim Type of the permission grant
     * @param {string} claimValue The Claim Type of the permission grant
     */
    getClaimPermissionGrants(claimType, claimValue) {
        return this.$http({
            method: "GET",
            url: `${this.apexApiUrl}/claims/${btoa(claimType)}/${claimValue}/permissions`
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Upsert the value of permission based on claim type,value and permission key
     * @param {number} claimType The Claim Type of the permission grant
     * @param {string} claimValue The Claim Type of the permission grant
     * @param {string} permissionKey The Permission Key of the permission grant
     * @param {string} request The value(boolean) of the permission grant
     */
    putClaimPermissionGrant(claimType, claimValue, permissionKey, request) {
        const url = claimValue
            ? `claims/${btoa(claimType)}/${claimValue}/permissions/${btoa(permissionKey)}`
            : `claims/${btoa(claimType)}/permissions/${btoa(permissionKey)}`
        return this.$http({
            method: "PUT",
            url: `${this.apexApiUrl}/${url}`,
            data: request
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Clear the permissions set for claimType and claimValue permission
     * @param {number} claimType The Claim Type of the permission grant
     * @param {string} claimValue The Claim Type of the permission grant
     * @param {string} permissionKey The Permission Key of the permission grant
     */
    clearClaimPermissionGrant(claimType, claimValue, permissionKey) {
        return this.$http({
            method: "DELETE",
            url: `${this.apexApiUrl}/claims/${btoa(claimType)}/${claimValue}/permissions/${btoa(permissionKey)}`
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Get list of all claim permission grants
     */
    getAllClaimPermissions() {
        return this.$http({
            method: "GET",
            url: `${this.apexApiUrl}/claims/all/permissions`
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
    * Set employee permission for onboarding
    * @param {any} employeeID The employee to whom onboarding permissions should be granted
    */
    setOnboardingFormPermissions(employeeID) {
        return this.$http({
            method: "GET",
            url: `${this.apexApiUrl}/setonboarding/${employeeID}`
        }).then(response => response.data)
    }

    /**
   * Set employee permission for onboarding
   * @param {any} employeeID The employee to whom onboarding permissions should be granted
   */
    revokeOnboardingFormPermissions(employeeID) {
        return this.$http({
            method: "GET",
            url: `${this.apexApiUrl}/revokeonboarding/${employeeID}`
        }).then(response => response.data)
    }
}
